import React, { memo, useRef, useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import useClickAndDragScroll from '../hooks/useClickAndDragScroll';
import './BucketNavbar.css';

const BucketNavbar = memo(forwardRef(({
  bucketOrder,
  buckets,
  onBucketClick,
  isTimerActive,
  hiddenBuckets = new Set(),
  onToggleBucketVisibility,
  onReorder
}, ref) => {
  const buttonRefs = useRef({});
  const [isMobile, setIsMobile] = useState(false);
  const navbarRef = useClickAndDragScroll();

  // Shift-Detection fuer Reorder-Modus
  const [isShiftDown, setIsShiftDown] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragHappenedRef = useRef(false);
  const dragStartPosRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Shift') setIsShiftDown(true); };
    const onKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsShiftDown(false);
        setDraggedId(null);
        setDragOverId(null);
        dragHappenedRef.current = false;
        dragStartPosRef.current = null;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  // Globaler pointermove: erkennt ob tatsaechlich gezogen wurde (Schwelle 5px)
  useEffect(() => {
    const onMove = (e) => {
      if (!dragStartPosRef.current) return;
      const dx = e.clientX - dragStartPosRef.current.x;
      const dy = e.clientY - dragStartPosRef.current.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        dragHappenedRef.current = true;
      }
    };
    // Globaler pointerup: Cleanup falls ausserhalb eines Buttons losgelassen wird
    const onUp = () => {
      if (draggedId) {
        setDraggedId(null);
        setDragOverId(null);
      }
      // dragHappenedRef wird im onClick-Handler zurueckgesetzt
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [draggedId]);

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    highlightButton: (bucketId) => {
      const button = buttonRefs.current[bucketId];
      if (button) {
        button.classList.add('bucket-nav-button--highlight');
        
        // On mobile, scroll the highlighted button into view
        if (isMobile) {
          button.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest', 
            inline: 'center' 
          });
        }
      }
    },
    clearHighlight: (bucketId) => {
      const button = buttonRefs.current[bucketId];
      if (button) {
        button.classList.remove('bucket-nav-button--highlight');
      }
    },
    clearAllHighlights: () => {
      Object.values(buttonRefs.current).forEach(button => {
        if (button) {
          button.classList.remove('bucket-nav-button--highlight');
        }
      });
    }
  }));

  if (!bucketOrder || !buckets) {
    return null;
  }

  return (
    <div ref={navbarRef} className={`bucket-navbar ${isTimerActive ? 'timer-active' : ''}`}>
      {bucketOrder.map((bucketId) => {
        const bucket = buckets[bucketId];
        if (!bucket) return null;
        
        return (
          <button
            key={`nav-bucket-${bucketId}`}
            ref={(el) => buttonRefs.current[bucketId] = el}
            data-bucket-id={bucketId}
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              if (!(e.shiftKey || isShiftDown)) return;
              dragStartPosRef.current = { x: e.clientX, y: e.clientY };
              dragHappenedRef.current = false;
              setDraggedId(bucketId);
              setDragOverId(null);
            }}
            onPointerEnter={() => {
              if (draggedId && draggedId !== bucketId) {
                setDragOverId(bucketId);
              }
            }}
            onPointerUp={() => {
              if (!draggedId) return;
              if (dragOverId && dragOverId !== draggedId) {
                const newOrder = [...bucketOrder];
                const fromIdx = newOrder.indexOf(draggedId);
                const toIdx = newOrder.indexOf(dragOverId);
                if (fromIdx !== -1 && toIdx !== -1) {
                  newOrder.splice(fromIdx, 1);
                  newOrder.splice(toIdx, 0, draggedId);
                  if (onReorder) onReorder(newOrder);
                }
              }
              setDraggedId(null);
              setDragOverId(null);
            }}
            onClick={(e) => {
              // Nach einem Drag keinen Click ausloesen (kein Scroll-to-Bucket)
              if (dragHappenedRef.current) {
                dragHappenedRef.current = false;
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (e.shiftKey) return; // Shift+Click ohne Drag = No-op (Reorder-Modus)
              e.preventDefault();
              e.stopPropagation();
              onBucketClick(bucketId);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onToggleBucketVisibility) {
                onToggleBucketVisibility(bucketId);
              }
            }}
            onTouchStart={(e) => {
              // Add touch feedback for mobile
              if (isMobile && e.currentTarget && e.currentTarget.style) {
                e.currentTarget.style.transform = 'scale(0.95)';
              }
            }}
            onTouchEnd={(e) => {
              // Remove touch feedback for mobile
              if (isMobile && e.currentTarget) {
                setTimeout(() => {
                  if (e.currentTarget && e.currentTarget.style) {
                    e.currentTarget.style.transform = '';
                  }
                }, 100);
              }
            }}
            type="button"
            title={isShiftDown ? 'Bucket verschieben (Shift gedrückt, auf anderen Bucket ziehen)' : undefined}
            className={`bucket-nav-button ${hiddenBuckets.has(bucketId) ? 'bucket-nav-button--hidden' : ''} ${isShiftDown ? 'bucket-nav-button--reorder' : ''} ${draggedId === bucketId ? 'bucket-nav-button--dragging' : ''} ${dragOverId === bucketId ? 'bucket-nav-button--drag-over' : ''}`}
          >
            <div className="bucket-nav-button__title">
              {bucket.title}
            </div>
          </button>
        );
      })}
    </div>
  );
}));

BucketNavbar.displayName = 'BucketNavbar';

export default BucketNavbar;